import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Switch } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";
import { DAY_CHOICES, Days, PickedWeekDayTimes } from "../constants/common";
import {
  getOfficialTimes,
  toLocalTimeConverter,
} from "../utils/date-time-utility";
import { replaceUnderScoreAddSpace } from "../utils/utils";
import ErrorHelper from "./ErrorWrapper";
import Flex from "./Flex";
import FormHeadChip from "./FormHeadText";
import MultiSelect from "./MultiSelect";
import Text from "./Text";

interface WeekTimePickerProps {
  pickedTimes?: PickedWeekDayTimes;
  setIsError?: any;
  onChange?: (value: PickedWeekDayTimes) => void;
}

const WeekTimePicker = ({
  pickedTimes,
  onChange,
  setIsError,
}: WeekTimePickerProps) => {
  // const [daySwitch, setDaySwitch] = useState<{ [k: string]: boolean }>(
  //   cloneDeep(SWITCH_DAYS)
  // );
  const [weeklyOffDay, setWeeklyOffDay] = useState<string[] | null>();
  const { startTime, endTime } = getOfficialTimes();
  /** to manage the expand and collapse of accordion
   *  in some case accordion should preExtended
   */
  const [expanded, setExpanded] = useState<{ [k: string]: boolean }>(
    /** selected Day (Active day) item accordion should open */
    Object.entries(pickedTimes ?? {})?.reduce((acc, [curDay, curDayData]) => {
      acc[curDay] = !!curDayData?.length;
      return acc;
    }, {} as any)
  );

  useEffect(() => {
    setExpanded(
      Object.entries(pickedTimes ?? {})?.reduce((acc, [curDay, curDayData]) => {
        acc[curDay] = !!curDayData?.length;
        return acc;
      }, {} as any)
    );
  }, [pickedTimes]);

  function changeHandler(
    day: Days,
    value: dayjs.Dayjs | null,
    ind: number,
    key: "open" | "close"
  ) {
    //early return by check working day slot times are not present
    if (!pickedTimes || !pickedTimes[day]) return;

    const copiedPickedTimes = cloneDeep(pickedTimes);

    // as we are early check - assuming that day data is present in pickedTimes
    copiedPickedTimes[day]![ind] = {
      ...(copiedPickedTimes?.[day]?.[ind] ?? {}),
      [key]: value?.toISOString(),
    };

    onChange?.(copiedPickedTimes);
  }

  return (
    <Flex gap={"10px"} flexDirection={"column"}>
      <Box width={"30%"}>
        <FormHeadChip mb={"15px"} variant="body2">
          WEEKLY OFF DAYS
        </FormHeadChip>
        <MultiSelect
          optionWithCheckbox
          value={weeklyOffDay ?? undefined}
          options={DAY_CHOICES}
          placeholder="Select Weekly Off Days (Max 2)"
          onChange={(days) => {
            // Null item of days selected in weeklyOff Days
            days?.forEach((it) => {
              onChange?.({ ...pickedTimes, [it]: null });

              expanded[it] = false;
            });
            setWeeklyOffDay(days);
          }}
        />
        <FormHeadChip mt={"15px"} variant="body2">
          WORKING HOURS
        </FormHeadChip>
      </Box>

      <Flex gap={"12px"} flexDirection={"column"}>
        {Object.values(Days)
          .filter((it) => !weeklyOffDay?.includes(it))
          ?.map((day, index) => {
            /** find the day item obj rather than just rely on index */
            // const day_item = expanded?.find((it) =>
            //   Object.keys(it).some((k) => k === day)
            // );

            return (
              <Accordion
                key={index}
                variant="outlined"
                expanded={!!expanded[day]}
                onChange={(e: any, newExpanded) => {
                  e.stopPropagation();

                  if (e.target?.id === "switch") return;

                  const copiedExpanded = cloneDeep(expanded);
                  /**
                   * check that item is selected or not
                   * should not to expand - non selected item
                   */
                  if (!!pickedTimes?.[day]) copiedExpanded[day] = newExpanded;

                  setExpanded(copiedExpanded);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Flex gap={"10px"} width={"100px"}>
                    <Switch
                      id="switch"
                      checked={!!pickedTimes?.[day]}
                      onChange={(e) => {
                        e.stopPropagation();

                        const copiedPickedTimes = cloneDeep(pickedTimes);
                        let newTime = undefined;

                        /**
                         * handle check switch without expanding-accordion
                         */
                        // setDaySwitch((prev) => ({
                        //   ...prev,
                        //   [day]: e.target.checked ?? null,
                        // }));

                        const copiedExpanded = cloneDeep(expanded);

                        copiedExpanded[day] = e.target.checked;

                        setExpanded(copiedExpanded);

                        if (!e.target.checked) {
                          newTime = {
                            ...copiedPickedTimes,
                            [day]: undefined,
                          };
                        } else {
                          newTime = {
                            ...copiedPickedTimes,
                            [day]: [
                              {
                                open: dayjs(startTime).toISOString(),
                                close: dayjs(endTime).toISOString(),
                              },
                            ],
                          };
                        }

                        onChange?.(newTime);
                      }}
                      size="small"
                    />
                    <Text>{replaceUnderScoreAddSpace(day)}</Text>
                  </Flex>

                  {/* show scheduled times on accordion strip */}
                  <Flex ml={"40px"} gap={"10px"}>
                    {pickedTimes?.[day]?.map(
                      (it, idx) =>
                        it.open &&
                        it?.close && (
                          <Flex gap={"8px"} key={idx} alignItems={"center"}>
                            <CircleIcon
                              sx={{ color: "#1A1A1A33", fontSize: "10px" }}
                            />
                            {<Text>{toLocalTimeConverter(it?.open)}</Text>}-
                            {<Text>{toLocalTimeConverter(it?.close)}</Text>}
                          </Flex>
                        )
                    )}
                  </Flex>
                </AccordionSummary>

                {/* multiple time Arr manage section */}
                <AccordionDetails>
                  {pickedTimes?.[day]?.map((it, ind) => {
                    /**
                     * exclude for first item
                     * user may need to select any time at first item
                     */
                    const isMultipleTimesToSetMinTime =
                      pickedTimes && pickedTimes?.[day]?.length && ind !== 0;
                    /** pick current date item from pickedTime Arr and add for check multiple item should present */

                    const validateFrom = () => {
                      if (
                        isMultipleTimesToSetMinTime &&
                        dayjs(pickedTimes?.[day]?.[ind]?.open).isAfter(
                          dayjs(pickedTimes?.[day]?.[ind]?.close)
                        )
                      ) {
                        setIsError(true);
                        return "Opening time cannot be in future";
                      } else if (
                        dayjs(pickedTimes?.[day]?.[ind]?.open).isSame(
                          dayjs(pickedTimes?.[day]?.[ind]?.close)
                        )
                      ) {
                        setIsError(true);
                        return "Opening and closing times cannot be the same";
                      }

                      setIsError(false);
                    };

                    const validateTo = () => {
                      if (
                        dayjs(pickedTimes?.[day]?.[ind]?.open).isSame(
                          dayjs(pickedTimes?.[day]?.[ind]?.close)
                        )
                      ) {
                        setIsError(true);
                        return "Opening and closing times cannot be the same";
                      } else if (
                        dayjs(pickedTimes?.[day]?.[ind]?.open).isAfter(
                          dayjs(pickedTimes?.[day]?.[ind]?.close)
                        )
                      ) {
                        setIsError(true);
                        return "Closing time cannot be in past";
                      }

                      setIsError(false);
                    };

                    return (
                      <Flex key={ind} gap={"20px"} alignItems={"center"}>
                        <Flex gap={"20px"} alignItems={"start"} mb={1.5}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Flex
                              alignItems={"start"}
                              justifyContent={"center"}
                            >
                              <ErrorHelper error={validateFrom()}>
                                <TimePicker
                                  closeOnSelect={false}
                                  label="From"
                                  // minTime={
                                  //   isMultipleTimesToSetMinTime
                                  //     ? /** ind - 1  check the upper items last close time to set the minTime  */
                                  //       dayjs(
                                  //         pickedTimes?.[day]?.[ind - 1].close
                                  //       ).add(5, "minute")
                                  //     : undefined
                                  // }
                                  // disabled={ind !== 0 && !it.open}
                                  value={it?.open ? dayjs(it?.open) : startTime}
                                  onChange={(value) =>
                                    changeHandler(day, value, ind, "open")
                                  }
                                />
                              </ErrorHelper>
                            </Flex>
                          </LocalizationProvider>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Flex
                              alignItems={"center"}
                              justifyContent={"center"}
                            >
                              <ErrorHelper error={validateTo()}>
                                <TimePicker
                                  closeOnSelect={false}
                                  label="To"
                                  minTime={
                                    /** in this To case it depend on correspondence item open */
                                    ind === 0
                                      ? dayjs(
                                          pickedTimes?.[day]?.[0]?.open
                                        ).add(5, "minute")
                                      : isMultipleTimesToSetMinTime
                                        ? pickedTimes?.[day]?.[ind]?.open
                                          ? dayjs(
                                              pickedTimes?.[day]?.[ind]?.open
                                            ).add(5, "minute")
                                          : // dayjs(pickedTimes?.[day]?.[ind].open)
                                            undefined
                                        : undefined
                                  }
                                  // disabled={ind !== 0 && !it?.close}
                                  value={it?.close ? dayjs(it?.close) : endTime}
                                  onChange={(value) =>
                                    changeHandler(day, value, ind, "close")
                                  }
                                />
                              </ErrorHelper>
                            </Flex>
                          </LocalizationProvider>
                        </Flex>
                        {/* add button to add multiple time items in PickedTimeArr */}
                        {ind === 0 && (
                          <Button
                            sx={{ mb: 1.5 }}
                            startIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={() => {
                              const len = pickedTimes[day]?.length;
                              if (!len || len < 1) {
                                // "block user to select first working hour time "
                                return;
                              }
                              // future reference while last need to add validation on base of previous time
                              const lastTime = pickedTimes?.[day]?.[len - 1];

                              if (!(lastTime?.open && lastTime?.close)) return;

                              const newTimeDays = {
                                open: dayjs(startTime).toISOString(),
                                close: dayjs(endTime).toISOString(),
                              };

                              const copiedPickedTimes = cloneDeep(pickedTimes);

                              if (copiedPickedTimes?.[day]?.length)
                                copiedPickedTimes[day] = [
                                  ...(copiedPickedTimes[day] ?? []),
                                  newTimeDays,
                                ];
                              onChange?.(copiedPickedTimes);
                            }}
                          >
                            Add Working Time
                          </Button>
                        )}

                        {/* delete button to delete the cur Item from picked time arr */}
                        {ind !== 0 && (
                          <DeleteIcon
                            color="error"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              const copiedPickedTimes = cloneDeep(pickedTimes);
                              copiedPickedTimes?.[day]?.splice(ind, 1);
                              onChange?.(copiedPickedTimes);
                            }}
                          />
                        )}
                      </Flex>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Flex>
    </Flex>
  );
};

export default WeekTimePicker;
